import * as React from 'react';
import {
  AnalyticsTrigger,
  Button,
  Card,
  CardContent,
  Grid,
  GridColumn,
  Heading,
  Icon,
  Modal,
  ModalProvider,
  ModalTrigger,
  Notice,
  Separator,
  TextField
} from 'telia-front-react';
import { TranslationsContext } from '@teliaee/telia-translate';
import { Segment } from '../../stores/SegmentStore';
import { renderCurrency } from '../../util/PriceUtil';
import { Country, Package } from '../RoamingInterfaces';
import SmsLink from './SmsLink';
import config from '../../Config';
import { getPackagePriceForSegment, getSmsMessageContent } from '../../util/RoamingPackagesUtil';
import LanguageStore from '../../stores/LanguageStore';
import { inject, observer } from 'mobx-react';

interface PackageOrderingModalProps {
  segment: Segment;
  roamingPackage: Package;
  selectedCountry: Country;
  languageStore?: LanguageStore;
}

const PackageOrderingModal = (props: PackageOrderingModalProps): JSX.Element => {
  const { segment, selectedCountry, roamingPackage, languageStore } = props;
  const { translate } = React.useContext(TranslationsContext);
  const modalAnalyticsId = `Link - ${selectedCountry.isoCode} - ${roamingPackage.estKeywords[0]} ${roamingPackage.volume} - Tellin`;

  const renderCountryWithPriceCard = (): JSX.Element => {
    const roamingPackagePrice = getPackagePriceForSegment(roamingPackage, segment);
    return (
      <Card>
        <CardContent background="neutral-bg-level-2">
          <Icon modifiers="#service-internet" size="lg" />
          <Separator spacing="xsmall" color="invisible" />
          <Heading element="h3" className="h5">
            {translate(`roaming.country.package.modal.header.${roamingPackage.serviceShortName}`)}
          </Heading>
          <Separator spacing="xxsmall" color="invisible" />
          <Heading element="h4" className="h3">
            {selectedCountry.name}
          </Heading>
          <Separator spacing="xsmall" color="invisible" />
          <Heading element="h5" className="h4">
            {`${roamingPackagePrice} ${renderCurrency(segment !== 'BUSINESS', roamingPackagePrice !== '0', translate, '€')}`}
          </Heading>
        </CardContent>
      </Card>
    );
  };

  const renderSmsInfo = (): JSX.Element => {
    const modalSmsContent = getSmsMessageContent(roamingPackage, languageStore.currentLanguage);
    return (
      <p>
        <Separator spacing="xsmall" color="invisible" />
        <Heading element="h5" className="h4">
          {translate('roaming.country.package.modal.send-sms-label')} <strong>{translate('roaming.country.package.modal.send-sms-number')}</strong>
        </Heading>
        <Separator spacing="xsmall" color="invisible" />
        <Grid>
          <GridColumn width={['md-10', 'lg-5']}>
            <TextField
              id="order-internet"
              label={translate('roaming.country.package.modal.sms-content')}
              value={modalSmsContent}
              isReadOnly={true}
              size="medium"
            />
            <SmsLink
              number={translate('roaming.country.package.modal.send-sms-number')}
              label={translate('roaming.country.package.modal.generate-sms')}
              content={modalSmsContent}
            />
          </GridColumn>
        </Grid>
      </p>
    );
  };

  const renderExtraInfo = (): JSX.Element => {
    const orderForOtherNumberAnalyticsId = `Link - ${selectedCountry.isoCode} - ${roamingPackage.estKeywords[0]} ${roamingPackage.volume} - Telli teisele numbrile`;
    return (
      <p>
        <Separator spacing="xsmall" color="invisible" />
        <Notice size="small">{translate(`roaming.country.package.modal.notice-text.${roamingPackage.serviceShortName}`)}</Notice>
        <Separator spacing="xsmall" color="invisible" />
        <AnalyticsTrigger trigger={{ id: orderForOtherNumberAnalyticsId, callback: 'onClick' }}>
          <Button
            text={translate('roaming.country.package.modal.orderForOtherNumber')}
            link={true}
            url={translate('roaming.country.package.modal.orderForOtherNumber.link', {
              baseUrl: config.teliaWebUrl
            })}
            target="_blank"
            iconRight="#arrow-right"
          />
        </AnalyticsTrigger>
      </p>
    );
  };

  const getModalContent = (): JSX.Element => {
    return (
      <>
        {renderCountryWithPriceCard()}
        {renderSmsInfo()}
        {renderExtraInfo()}
      </>
    );
  };

  return (
    <AnalyticsTrigger trigger={{ id: modalAnalyticsId, callback: 'onOpen' }}>
      <ModalProvider id={`${roamingPackage.estKeywords[0]}-package-ordering-modal`}>
        <ModalTrigger>
          <Button text={translate('roaming.country.package.order')} type="expressive" id="order-package" block={true} />
        </ModalTrigger>
        <Modal size="8-col">{getModalContent()}</Modal>
      </ModalProvider>
    </AnalyticsTrigger>
  );
};

export default inject('languageStore')(observer(PackageOrderingModal));
